class Slideshow {
  constructor(container) {
    this.container = container;
    this.slides = this.container.querySelectorAll('.slideshow__slide');
    this.dots = this.container.querySelectorAll('.slideshow__dot');
    this.prevButton = this.container.querySelector('.slideshow__arrow--prev');
    this.nextButton = this.container.querySelector('.slideshow__arrow--next');
    this.currentSlide = 0;
    this.autoplayEnabled = this.container.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.container.dataset.autoplaySpeed) || 5000;
    this.transitionType = this.container.dataset.transition || 'fade';
    this.slideHeight = this.container.dataset.height || 'adapt';
    this.autoplayTimer = null;
    this.isTransitioning = false;
    this.isHeightSet = false;
    
    // Video related properties
    this.videoPlayers = {};
    this.videoAPIReady = {
      youtube: false,
      vimeo: false
    };
    
    // Loading state properties
    this.isLoading = true;
    this.loadedImages = new Set();
    this.loadedVideos = new Set();

    // Drag related properties
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.dragThreshold = 50;
    
    // Default values for empty/blank slides
    this.defaultAspectRatio = 16/9;
    this.defaultMinHeight = 480;

    this.init();
  }

  init() {
    // Add loading class
    this.container.classList.add('is-loading');
    
    // Set up image loading handlers
    this.preloadMedia();
    
    // Load video APIs if needed
    this.loadVideoAPIs();
    
    // Apply initial slide height settings
    this.updateSlideHeight();
    
    // Set up initial state
    this.showSlide(0);

    // Add event listeners
    this.setupEventListeners();

    // Add customizer event handlers
    this.setupCustomizerEvents();

    // Start autoplay if enabled
    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }

  setupCustomizerEvents() {
    // Handle block select in customizer
    document.addEventListener('shopify:block:select', (event) => {
      // Check if the event belongs to this slideshow
      if (event.target.closest(`[data-section-id="${this.container.dataset.sectionId}"]`)) {
        const slide = event.target.closest('.slideshow__slide');
        if (slide) {
          const index = parseInt(slide.dataset.slideIndex);
          // Pause autoplay when a block is selected
          this.pauseAutoplay();
          // Show the selected slide without animation
          this.goToSlide(index, true);
        }
      }
    });

    // Handle block deselect in customizer
    document.addEventListener('shopify:block:deselect', (event) => {
      // Check if the event belongs to this slideshow
      if (event.target.closest(`[data-section-id="${this.container.dataset.sectionId}"]`)) {
        // Resume autoplay if it was enabled
        this.resumeAutoplay();
      }
    });

    // Handle section settings changes
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target === this.container.closest('.shopify-section')) {
        this.updateSlideHeight();
      }
    });
    
    // Listen for slide height setting changes
    document.addEventListener('shopify:block:select', (event) => {
      if (event.target.closest(`[data-section-id="${this.container.dataset.sectionId}"]`)) {
        setTimeout(() => {
          this.updateSlideHeight();
          this.updateVideoContainerDimensions();
        }, 100);
      }
    });
    
    // Update when any section setting changes (including height setting)
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target === this.container.closest('.shopify-section')) {
        setTimeout(() => {
          this.updateSlideHeight();
          this.updateVideoContainerDimensions();
        }, 100);
      }
    });
  }

  loadVideoAPIs() {
    // Check if we have any YouTube videos
    const youtubeVideos = this.container.querySelectorAll('[data-youtube-id]');
    if (youtubeVideos.length > 0 && !window.YT) {
      // Load the YouTube API
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      
      // Set up the callback for when the API is ready
      window.onYouTubeIframeAPIReady = () => {
        this.videoAPIReady.youtube = true;
        this.initYouTubeVideos();
      };
    } else if (window.YT && window.YT.Player) {
      this.videoAPIReady.youtube = true;
      this.initYouTubeVideos();
    }
    
    // Check if we have any Vimeo videos
    const vimeoVideos = this.container.querySelectorAll('[data-vimeo-id]');
    if (vimeoVideos.length > 0 && !window.Vimeo) {
      // Load the Vimeo API
      const tag = document.createElement('script');
      tag.src = 'https://player.vimeo.com/api/player.js';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      
      // Set up a check for when the API is ready
      const checkVimeoAPI = setInterval(() => {
        if (window.Vimeo && window.Vimeo.Player) {
          clearInterval(checkVimeoAPI);
          this.videoAPIReady.vimeo = true;
          this.initVimeoVideos();
        }
      }, 100);
    } else if (window.Vimeo && window.Vimeo.Player) {
      this.videoAPIReady.vimeo = true;
      this.initVimeoVideos();
    }
  }

  initYouTubeVideos() {
    const youtubeVideos = this.container.querySelectorAll('[data-youtube-id]');
    youtubeVideos.forEach((video, index) => {
      const videoId = video.dataset.youtubeId;
      const autoplay = video.dataset.autoplay === 'true';
      const mute = video.dataset.mute === 'true';
      const loop = video.dataset.loop === 'true';
      const playOnVisible = video.dataset.playOnVisible === 'true';
      
      // Create a play button
      const playButton = this.createPlayButton(video);
      
      // Create the player
      const playerVars = {
        autoplay: autoplay ? 1 : 0,
        mute: mute ? 1 : 0,
        controls: 0,
        showinfo: 0,
        modestbranding: 1,
        rel: 0,
        loop: loop ? 1 : 0,
        playlist: loop ? videoId : '',
        playsinline: 1
      };
      
      if (!this.videoPlayers[`youtube-${videoId}`]) {
        // Create a container for the iframe
        const playerContainer = document.createElement('div');
        playerContainer.className = 'slideshow__youtube-container';
        video.appendChild(playerContainer);
        
        const player = new YT.Player(playerContainer, {
          videoId: videoId,
          playerVars: playerVars,
          events: {
            'onReady': (event) => {
              // Store the player
              this.videoPlayers[`youtube-${videoId}`] = event.target;
              
              // Handle video ready
              this.handleVideoReady(video);
              
              // Add click handler to play button
              if (playButton) {
                playButton.addEventListener('click', () => {
                  event.target.playVideo();
                  playButton.style.opacity = '0';
                  playButton.style.visibility = 'hidden';
                });
              }
            },
            'onStateChange': (event) => {
              // If the video ends and should loop
              if (event.data === YT.PlayerState.ENDED && loop) {
                event.target.playVideo();
              }
              
              // If the video is playing, hide the play button
              if (event.data === YT.PlayerState.PLAYING) {
                if (playButton) {
                  playButton.style.opacity = '0';
                  playButton.style.visibility = 'hidden';
                }
                
                // Hide the placeholder and cover
                const placeholder = video.querySelector('.slideshow__video-placeholder');
                if (placeholder) {
                  placeholder.style.opacity = '0';
                  placeholder.style.zIndex = '0';
                }
                
                const cover = video.closest('.slideshow__slide').querySelector('.slideshow__video-cover');
                if (cover) {
                  cover.style.opacity = '0';
                  cover.style.zIndex = '0';
                }
              }
            }
          }
        });
      }
    });
  }

  initVimeoVideos() {
    const vimeoVideos = this.container.querySelectorAll('[data-vimeo-id]');
    vimeoVideos.forEach((video, index) => {
      const videoId = video.dataset.vimeoId;
      const autoplay = video.dataset.autoplay === 'true';
      const mute = video.dataset.mute === 'true';
      const loop = video.dataset.loop === 'true';
      const playOnVisible = video.dataset.playOnVisible === 'true';
      
      // Create a play button
      const playButton = this.createPlayButton(video);
      
      // Create the player
      if (!this.videoPlayers[`vimeo-${videoId}`]) {
        // Create a container for the iframe
        const playerContainer = document.createElement('div');
        playerContainer.className = 'slideshow__vimeo-container';
        video.appendChild(playerContainer);
        
        const player = new Vimeo.Player(playerContainer, {
          id: videoId,
          background: autoplay,
          muted: mute,
          loop: loop,
          autoplay: autoplay,
          transparent: false,
          playsinline: true,
          title: false,
          byline: false,
          portrait: false
        });
        
        // Store the player
        this.videoPlayers[`vimeo-${videoId}`] = player;
        
        // When the player is ready
        player.ready().then(() => {
          // Handle video ready
          this.handleVideoReady(video);
          
          // Add click handler to play button
          if (playButton) {
            playButton.addEventListener('click', () => {
              player.play();
              playButton.style.opacity = '0';
              playButton.style.visibility = 'hidden';
            });
          }
        });
        
        // When the video plays
        player.on('play', () => {
          if (playButton) {
            playButton.style.opacity = '0';
            playButton.style.visibility = 'hidden';
          }
          
          // Hide the placeholder and cover
          const placeholder = video.querySelector('.slideshow__video-placeholder');
          if (placeholder) {
            placeholder.style.opacity = '0';
            placeholder.style.zIndex = '0';
          }
          
          const cover = video.closest('.slideshow__slide').querySelector('.slideshow__video-cover');
          if (cover) {
            cover.style.opacity = '0';
            cover.style.zIndex = '0';
          }
        });
      }
    });
  }

  createPlayButton(videoContainer) {
    // Check if a play button already exists
    if (videoContainer.querySelector('.slideshow__video-play-button')) {
      return videoContainer.querySelector('.slideshow__video-play-button');
    }
    
    // Create the play button
    const playButton = document.createElement('div');
    playButton.className = 'slideshow__video-play-button';
    
    // Create the play icon
    const playIcon = document.createElement('div');
    playIcon.className = 'slideshow__video-play-icon';
    
    // Add the icon to the button
    playButton.appendChild(playIcon);
    
    // Add the button to the video container
    videoContainer.appendChild(playButton);
    
    return playButton;
  }

  handleVideoReady(videoContainer) {
    // Add to set of loaded videos
    this.loadedVideos.add(videoContainer);
    
    // Check if all media is loaded
    if (this.loadedImages.size + this.loadedVideos.size >= this.slides.length) {
      this.finishLoading();
    }
  }

  preloadMedia() {
    const images = Array.from(this.container.querySelectorAll('.slideshow__image'));
    const videos = Array.from(this.container.querySelectorAll('.slideshow__video'));
    
    // If there are no images or videos, finish loading
    if (images.length === 0 && videos.length === 0) {
      this.finishLoading();
      return;
    }
    
    // Add loading class to all images
    images.forEach(img => img.classList.add('loading'));
    
    // Handle each image load
    images.forEach(img => {
      if (img.complete) {
        this.handleImageLoad(img);
      } else {
        img.addEventListener('load', () => this.handleImageLoad(img));
      }
      
      // Handle image error
      img.addEventListener('error', () => this.handleImageError(img));
    });
    
    // Handle HTML5 videos
    videos.forEach(video => {
      if (video.tagName === 'VIDEO') {
        // If the video has enough data to play
        if (video.readyState >= 2) {
          this.handleVideoReady(video);
        } else {
          // Wait for enough data to play
          video.addEventListener('canplay', () => this.handleVideoReady(video));
        }
        
        // Handle video error
        video.addEventListener('error', () => this.handleVideoError(video));
      }
    });
  }

  handleImageLoad(img) {
    // Remove loading class from image
    img.classList.remove('loading');
    
    // Add to set of loaded images
    this.loadedImages.add(img);
    
    // Check if all media is loaded
    if (this.loadedImages.size + this.loadedVideos.size >= this.slides.length) {
      this.finishLoading();
    }
    
    // Update height based on first image if it's loaded and using adaptive height
    if (img === this.slides[0].querySelector('.slideshow__image') && this.slideHeight === 'adapt') {
      this.updateSlideHeight(img);
    }
  }

  handleImageError(img) {
    // Remove loading class and add error state
    img.classList.remove('loading');
    img.classList.add('error');
    
    // Still count as "loaded" to prevent infinite loading state
    this.loadedImages.add(img);
    
    if (this.loadedImages.size + this.loadedVideos.size >= this.slides.length) {
      this.finishLoading();
    }
  }

  handleVideoError(video) {
    // Add error state
    video.classList.add('error');
    
    // Still count as "loaded" to prevent infinite loading state
    this.loadedVideos.add(video);
    
    if (this.loadedImages.size + this.loadedVideos.size >= this.slides.length) {
      this.finishLoading();
    }
  }

  finishLoading() {
    // Remove loading class from container
    this.container.classList.remove('is-loading');
    this.isLoading = false;
  }

  setupEventListeners() {
    // Navigation arrows
    this.prevButton?.addEventListener('click', () => this.prevSlide());
    this.nextButton?.addEventListener('click', () => this.nextSlide());

    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Mouse drag events
    this.container.addEventListener('mousedown', this.handleDragStart.bind(this));
    this.container.addEventListener('mousemove', this.handleDragMove.bind(this));
    this.container.addEventListener('mouseup', this.handleDragEnd.bind(this));
    this.container.addEventListener('mouseleave', this.handleDragEnd.bind(this));

    // Prevent default drag behavior
    this.container.addEventListener('dragstart', (e) => e.preventDefault());

    // Pause autoplay on hover
    this.container.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.container.addEventListener('mouseleave', () => this.resumeAutoplay());

    // Touch events
    let touchStartX = 0;
    let touchEndX = 0;

    this.container.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.container.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleTouchEnd(touchStartX, touchEndX);
    }, { passive: true });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });

    // Update on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
        this.pauseAllVideos();
      } else {
        this.resumeAutoplay();
        this.playCurrentVideo();
      }
    });

    // Resize handler with debounce
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (this.slideHeight === 'adapt') {
          this.updateSlideHeight();
        }
      }, 250);
    });
  }

  // Video playback control methods
  playCurrentVideo() {
    const currentSlide = this.slides[this.currentSlide];
    if (!currentSlide) return;
    
    // Check if the current slide has a video
    if (currentSlide.classList.contains('slideshow__slide--video')) {
      const youtubeId = currentSlide.dataset.videoId;
      const videoType = currentSlide.dataset.videoType;
      
      if (videoType === 'youtube' && this.videoPlayers[`youtube-${youtubeId}`]) {
        this.videoPlayers[`youtube-${youtubeId}`].playVideo();
      } else if (videoType === 'vimeo' && this.videoPlayers[`vimeo-${youtubeId}`]) {
        this.videoPlayers[`vimeo-${youtubeId}`].play();
      } else if (videoType === 'mp4') {
        const videoElement = currentSlide.querySelector('video');
        if (videoElement) {
          videoElement.play().catch(error => {
            // Auto-play was prevented, show play button
            const playButton = currentSlide.querySelector('.slideshow__video-play-button');
            if (playButton) {
              playButton.style.opacity = '1';
              playButton.style.visibility = 'visible';
            }
          });
        }
      }
    }
  }

  pauseCurrentVideo() {
    const currentSlide = this.slides[this.currentSlide];
    if (!currentSlide) return;
    
    // Check if the current slide has a video
    if (currentSlide.classList.contains('slideshow__slide--video')) {
      const youtubeId = currentSlide.dataset.videoId;
      const videoType = currentSlide.dataset.videoType;
      
      if (videoType === 'youtube' && this.videoPlayers[`youtube-${youtubeId}`]) {
        this.videoPlayers[`youtube-${youtubeId}`].pauseVideo();
      } else if (videoType === 'vimeo' && this.videoPlayers[`vimeo-${youtubeId}`]) {
        this.videoPlayers[`vimeo-${youtubeId}`].pause();
      } else if (videoType === 'mp4') {
        const videoElement = currentSlide.querySelector('video');
        if (videoElement) {
          videoElement.pause();
        }
      }
    }
  }

  pauseAllVideos() {
    // Pause all YouTube videos
    Object.keys(this.videoPlayers).forEach(key => {
      if (key.startsWith('youtube-') && this.videoPlayers[key].pauseVideo) {
        this.videoPlayers[key].pauseVideo();
      } else if (key.startsWith('vimeo-') && this.videoPlayers[key].pause) {
        this.videoPlayers[key].pause();
      }
    });
    
    // Pause all HTML5 videos
    const videos = this.container.querySelectorAll('video');
    videos.forEach(video => {
      video.pause();
    });
  }

  handleDragStart(e) {
    if (this.isTransitioning) return;
    this.isDragging = true;
    this.startPos = e.clientX;
    this.container.style.cursor = 'grabbing';
    
    // Pause autoplay while dragging
    this.pauseAutoplay();
    
    // Pause any playing videos
    this.pauseCurrentVideo();
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    const currentPosition = e.clientX;
    const diff = currentPosition - this.startPos;

    // Update current translate
    this.currentTranslate = this.prevTranslate + diff;

    // Optional: Add visual feedback during drag
    const currentSlideEl = this.slides[this.currentSlide];
    if (currentSlideEl) {
      currentSlideEl.style.transform = `translateX(${diff}px)`;
    }
  }

  handleDragEnd(e) {
    if (!this.isDragging) return;

    const movedBy = e.clientX - this.startPos;
    
    // Reset styles
    this.container.style.cursor = '';
    const currentSlideEl = this.slides[this.currentSlide];
    if (currentSlideEl) {
      currentSlideEl.style.transform = '';
    }

    // Determine if slide should change
    if (Math.abs(movedBy) > this.dragThreshold) {
      if (movedBy < 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }

    // Reset drag state
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;

    // Resume autoplay if enabled
    this.resumeAutoplay();
  }

  handleTouchEnd(startX, endX) {
    const threshold = 50; // minimum distance for swipe
    const diff = startX - endX;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.nextSlide(); // Swipe left
      } else {
        this.prevSlide(); // Swipe right
      }
    }
  }

  updateSlideHeight(image = null) {
    // If not using adaptive height, apply the fixed height based on setting
    if (this.slideHeight !== 'adapt') {
      // The container already has proper CSS classes applied for small/medium/large
      // via data-height attribute, but we'll update any video containers
      this.updateVideoContainerDimensions();
      return;
    }

    if (!image) {
      // Try to find first valid image
      this.slides.forEach((slide) => {
        const slideImage = slide.querySelector('.slideshow__image');
        if (!image && slideImage && slideImage.complete && !slideImage.classList.contains('error')) {
          image = slideImage;
        }
      });
    }

    if (!image) {
      // If no image is found but we have videos, use default aspect ratio
      if (this.container.querySelectorAll('.slideshow__video').length > 0) {
        const containerWidth = this.container.clientWidth;
        let newHeight = containerWidth / this.defaultAspectRatio;
        newHeight = Math.max(this.defaultMinHeight, Math.min(newHeight, window.innerHeight));
        this.container.style.height = `${newHeight}px`;
        this.isHeightSet = true;
        this.updateVideoContainerDimensions();
      }
      return;
    }

    // Get the natural aspect ratio of the image
    const aspectRatio = image.naturalWidth / image.naturalHeight;
    if (!aspectRatio || aspectRatio === 0) {
      return;
    }
    
    // Get the current width of the container
    const containerWidth = this.container.clientWidth;
    
    // Calculate the height based on aspect ratio
    let newHeight = containerWidth / aspectRatio;
    
    // Apply minimum and maximum constraints
    newHeight = Math.max(this.defaultMinHeight, Math.min(newHeight, window.innerHeight));
    
    // Apply the height to the section
    this.container.style.height = `${newHeight}px`;
    this.isHeightSet = true;
    
    // Update video dimensions to match
    this.updateVideoContainerDimensions();
  }
  
  updateVideoContainerDimensions() {
    // Get all video containers
    const videoContainers = this.container.querySelectorAll('.slideshow__video-wrapper');
    if (!videoContainers.length) return;
    
    // Get the slideshow container dimensions
    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;
    
    videoContainers.forEach(container => {
      // Adjust YouTube/Vimeo iframes
      const iframes = container.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        // Calculate dimensions to maintain aspect ratio while covering the container
        let width, height;
        const videoRatio = 16/9; // Default video ratio
        const containerRatio = containerWidth / containerHeight;
        
        if (containerRatio > videoRatio) {
          // Container is wider than video
          width = containerWidth;
          height = width / videoRatio;
        } else {
          // Container is taller than video
          height = containerHeight;
          width = height * videoRatio;
        }
        
        // Apply styles to center the video
        iframe.style.width = `${width}px`;
        iframe.style.height = `${height}px`;
        iframe.style.position = 'absolute';
        iframe.style.top = '50%';
        iframe.style.left = '50%';
        iframe.style.transform = 'translate(-50%, -50%)';
        iframe.style.maxWidth = 'none';
      });
      
      // Adjust HTML5 videos
      const videos = container.querySelectorAll('video');
      videos.forEach(video => {
        // Use object-fit: cover for HTML5 videos
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
        video.style.position = 'absolute';
        video.style.top = '50%';
        video.style.left = '50%';
        video.style.transform = 'translate(-50%, -50%)';
      });
    });
  }

  showSlide(index) {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    // Pause any playing videos
    this.pauseCurrentVideo();

    // Remove active class from current slide and dot
    this.slides[this.currentSlide].classList.remove('active');
    this.dots[this.currentSlide].classList.remove('active');

    // Add active class to new slide and dot
    this.slides[index].classList.add('active');
    this.dots[index].classList.add('active');

    // Update progress bar
    this.updateProgressBar(index);

    // Update current slide index
    this.currentSlide = index;

    // Play video if the new slide has one
    setTimeout(() => {
      this.playCurrentVideo();
    }, 300);

    // Reset transition lock after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
    }, 500);
  }

  updateProgressBar(index) {
    // Reset all progress bars
    this.dots.forEach(dot => {
      const progress = dot.querySelector('.slideshow__dot-progress');
      if (progress) {
        progress.style.transform = 'scaleX(0)';
      }
    });

    // Animate current progress bar
    if (this.autoplayEnabled) {
      const currentProgress = this.dots[index].querySelector('.slideshow__dot-progress');
      if (currentProgress) {
        currentProgress.style.transform = 'scaleX(1)';
        currentProgress.style.transition = `transform ${this.autoplaySpeed}ms linear`;
      }
    }
  }

  goToSlide(index, skipAnimation = false) {
    if (index === this.currentSlide || this.isTransitioning) return;
    
    this.pauseAutoplay();

    if (skipAnimation) {
      // Add a class to disable transitions temporarily
      this.container.classList.add('no-transition');
    }

    this.showSlide(index);

    if (skipAnimation) {
      // Remove the no-transition class after a brief delay
      setTimeout(() => {
        this.container.classList.remove('no-transition');
      }, 10);
    }

    this.resumeAutoplay();
  }

  nextSlide() {
    const next = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(next);
  }

  prevSlide() {
    const prev = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
    this.goToSlide(prev);
  }

  startAutoplay() {
    if (!this.autoplayEnabled) return;
    
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resumeAutoplay() {
    if (this.autoplayEnabled && !this.autoplayTimer) {
      this.startAutoplay();
    }
  }

  destroy() {
    this.pauseAutoplay();
    this.pauseAllVideos();
    
    // Clean up video players
    Object.keys(this.videoPlayers).forEach(key => {
      if (key.startsWith('youtube-') && this.videoPlayers[key].destroy) {
        this.videoPlayers[key].destroy();
      }
    });
    
    this.videoPlayers = {};
  }
}

// Initialize slideshows
document.addEventListener('DOMContentLoaded', () => {
  const slideshows = document.querySelectorAll('[data-section-type="slideshow"]');
  slideshows.forEach(slideshow => {
    new Slideshow(slideshow);
  });
});

  // Re-initialize on section load if using Shopify section rendering
document.addEventListener('shopify:section:load', (event) => {
  const slideshow = event.target.querySelector('[data-section-type="slideshow"]');
  if (slideshow) {
    new Slideshow(slideshow);
  }
});

// Update video dimensions when slideshow height setting changes
document.addEventListener('shopify:section:reorder', (event) => {
  const slideshow = event.target.querySelector('[data-section-type="slideshow"]');
  if (slideshow && slideshow.slideshowInstance) {
    slideshow.slideshowInstance.updateVideoContainerDimensions();
  }
});

// Store slideshow instance reference on the DOM element for access in events
window.addEventListener('load', () => {
  const slideshows = document.querySelectorAll('[data-section-type="slideshow"]');
  slideshows.forEach(slideshow => {
    if (!slideshow.slideshowInstance) {
      const instance = new Slideshow(slideshow);
      slideshow.slideshowInstance = instance;
    }
  });
});